// skills/alchemy/alchemyUI.js
// Alchemy UI - Gatherer-style card layout

import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import { alchemySkill } from './alchemy.js';
import { HERB_CLEANING, POTION_RECIPES, MORTARS } from './alchemyData.js';
import { getItem, getItemName, getItemIcon } from '../../data/items/index.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import logger from '../../utils/logger.js';
import { formatWithCommas } from '../../utils/format.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen, getActiveSkillId } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus } from '../../systems/talentTree/talentTree.js';
import { getCorruptionSpeedMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { getSkillIcon } from '../../core/skillRegistry.js';
import { getAtlasBgStyle } from '../../data/atlasManifest.js';

// Current tab state (persisted in localStorage)
const ALCHEMY_TAB_KEY = 'idleRealms_tab_alchemy';
let currentTab = localStorage.getItem(ALCHEMY_TAB_KEY) || 'cleaning';

// Fire icon sprite for ashes bonus (lazy — atlas loads after module import)
let _fireSpriteCache = null;
function getFireSpriteHTML() {
    if (_fireSpriteCache) return _fireSpriteCache;
    const sheet = 'assets/icons/ui_icons/fire.png';
    const atlas = getAtlasBgStyle(sheet, 16, 16);
    _fireSpriteCache = atlas
        ? `<span class="sprite-icon fire-sprite" style="${atlas.style}"></span>`
        : `<span class="sprite-icon fire-sprite" style="background-image: url('${sheet}'); background-position: 0px 0px; width: 16px; height: 16px; background-size: contain;"></span>`;
    return _fireSpriteCache;
}

// Category icons for potion effects
const CATEGORY_ICONS = {
    attack: '⚔️', defense: '🛡️', accuracy: '🎯', speed: '⚡', hybrid: '⚖️',
    crit: '💥', reduction: '🧱', evasion: '💨', magic: '✨', ranged: '🏹', all: '🌟'
};

/**
 * Format potion effects for display in card
 */
function formatPotionEffects(effects) {
    if (!effects) return null;

    const parts = [];
    const duration = effects.duration ? `${effects.duration / 60000}m` : '';

    if (effects.attackBonus) parts.push(`+${effects.attackBonus} Attack`);
    if (effects.defenseBonus) parts.push(`+${effects.defenseBonus} Defense`);
    if (effects.accuracyBonus) parts.push(`+${effects.accuracyBonus}% Accuracy`);
    if (effects.speedBonus) parts.push(`+${effects.speedBonus}% Attack Speed`);
    if (effects.critBonus) parts.push(`+${effects.critBonus}% Crit`);
    if (effects.damageReduction) parts.push(`-${effects.damageReduction}% Damage Taken`);
    if (effects.enemyAccuracyReduction) parts.push(`-${effects.enemyAccuracyReduction}% Enemy Acc`);

    // Check if all 3 damage bonuses are the same (Ascendant Concoction)
    const hasMelee = effects.meleeDamageBonus;
    const hasRanged = effects.rangedDamageBonus;
    const hasMagic = effects.magicDamageBonus;
    if (hasMelee && hasRanged && hasMagic && hasMelee === hasRanged && hasRanged === hasMagic) {
        // All same value - show as single "All Damage" line
        parts.push(`+${hasMelee}% All Damage`);
    } else {
        // Show individually
        if (hasMelee) parts.push(`+${hasMelee}% Melee Dmg`);
        if (hasMagic) parts.push(`+${hasMagic}% Magic Dmg`);
        if (hasRanged) parts.push(`+${hasRanged}% Ranged Dmg`);
    }

    if (effects.glyphSaveChance) parts.push(`${effects.glyphSaveChance}% Glyph Save`);
    if (effects.ammoSaveChance) parts.push(`${effects.ammoSaveChance}% Ammo Save`);
    if (effects.corruptionBonus) parts.push(`+${effects.corruptionBonus}% Corruption Proc`);

    return {
        category: effects.category,
        icon: CATEGORY_ICONS[effects.category] || '🧪',
        duration: duration,
        effects: parts
    };
}

/**
 * Get combined item count from inventory + BloomHold
 */
function getItemCountWithBloomhold(itemId) {
    const inventoryCount = gameState.getItemCount(itemId);
    const bloomholdCount = gameState.isBloomholdUnlocked()
        ? gameState.getBloomholdItemCount(itemId)
        : 0;
    return {
        inventory: inventoryCount,
        bloomhold: bloomholdCount,
        total: inventoryCount + bloomholdCount
    };
}

// Alias for backward compatibility
function getHerbCount(itemId) {
    return getItemCountWithBloomhold(itemId);
}

/**
 * Render compact header with mortar upgrades
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.ALCHEMY);
    const xpPerHour = getXPPerHour(SKILLS.ALCHEMY);
    const { levelDisplay, progressPercent, progressText } = getSkillProgressInfo(SKILLS.ALCHEMY);

    // Tool info
    const activeTool = alchemySkill.getActiveBuff();
    const mortars = Object.values(MORTARS);
    const currentIndex = mortars.findIndex(m => m.id === activeTool.id);
    const nextMortar = mortars[currentIndex + 1];
    const playerGold = gameState.getGold();

    let upgradeHtml = '';
    if (nextMortar) {
        const canAfford = playerGold >= nextMortar.price;
        if (skill.level < nextMortar.level) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextMortar.name} (${nextMortar.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextMortar.price}">${getItemIcon('coins')} ${formatWithCommas(nextMortar.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextMortar.level}</span>
                </span>
            `;
        } else {
            const priceClass = canAfford ? 'affordable' : 'cant-afford';
            upgradeHtml = `
                <span class="compact-upgrade available" onclick="window.alchemyUI.upgradeMortar('${nextMortar.id}')">
                    → ${nextMortar.name} (${nextMortar.speedMultiplier}x)
                    <span class="upgrade-cost ${priceClass}" data-price="${nextMortar.price}">${getItemIcon('coins')} ${formatWithCommas(nextMortar.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ Max</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.ALCHEMY)}</span>
                    <span class="compact-name">Alchemy</span>
                    ${renderSkillInfo(SKILLS.ALCHEMY)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.ALCHEMY)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Mortar:</span>
                    <span class="tool-current">${activeTool.name} (${activeTool.speedMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${progressPercent}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render a single herb cleaning card
 */
function renderHerbCard(recipe, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= recipe.level;
    const isActive = currentAction?.type === SKILLS.ALCHEMY &&
                    currentAction?.subType === 'cleaning' &&
                    currentAction?.recipe?.id === recipe.id;
    const progress = isActive ? currentAction.progress : 0;

    const counts = getHerbCount(recipe.inputItem);
    const hasMaterials = counts.total >= 1;
    const herbName = recipe.name.replace('Clean ', '');

    // Calculate effective time with all bonuses
    const speedMultiplier = alchemySkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.ALCHEMY);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.ALCHEMY);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = recipe.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.ALCHEMY);
    let effectiveXP = recipe.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Get item info
    const inputIcon = getItemIcon(recipe.inputItem);
    const outputDrop = recipe.drops[0];
    const outputIcon = getItemIcon(outputDrop.itemId);
    const outputName = getItemName(outputDrop.itemId);

    const cardClasses = [
        'herb-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasMaterials && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    // Source breakdown for tooltip
    const sourceBreakdown = counts.bloomhold > 0
        ? `(${counts.inventory}+${counts.bloomhold}🌱)`
        : '';

    return `
        <div class="${cardClasses}"
             data-herb-id="${recipe.id}"
             style="--herb-color: ${recipe.color}">

            <div class="herb-card-header">
                <span class="herb-card-level">${isUnlocked ? `Lvl ${recipe.level}` : `Req: ${recipe.level}`}</span>
                <span class="herb-card-icon">${inputIcon}</span>
                <span class="herb-card-name">${herbName}</span>
            </div>

            <div class="herb-card-info">
                <div class="herb-stat">
                    <span class="stat-icon alchemy-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp">${recipe.xp}</span>
                </div>
                <div class="herb-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="herb-materials">
                <div class="materials-label">Input:</div>
                <div class="material-row ${!hasMaterials ? 'missing' : ''}">
                    <span class="material-icon">${inputIcon}</span>
                    <span class="material-name">Grimy ${herbName}</span>
                    <span class="material-count">${counts.total}/1 ${sourceBreakdown}</span>
                </div>
            </div>

            <div class="herb-output">
                <div class="output-label">Output:</div>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    <span class="output-qty">x${outputDrop.quantity}</span>
                </div>
            </div>

            <div class="herb-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.alchemyUI.toggleCleaning('${recipe.id}')">
                        ${isActive ? 'Stop' : 'Clean'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="herb-progress">
                <div class="herb-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render a single potion brewing card
 */
function renderPotionCard(recipe, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= recipe.level;
    const isActive = currentAction?.type === SKILLS.ALCHEMY &&
                    currentAction?.subType === 'potion' &&
                    currentAction?.recipe?.id === recipe.id;
    const progress = isActive ? currentAction.progress : 0;

    const herbCounts = getHerbCount(recipe.inputItem);
    const vialCounts = getItemCountWithBloomhold(recipe.secondaryItem);
    const hasHerbs = herbCounts.total >= recipe.inputQuantity;
    const hasVials = vialCounts.total >= recipe.secondaryQuantity;
    const hasMaterials = hasHerbs && hasVials;

    const herbName = getItemName(recipe.inputItem);
    const herbIcon = getItemIcon(recipe.inputItem);
    const vialName = getItemName(recipe.secondaryItem);
    const vialIcon = getItemIcon(recipe.secondaryItem);

    // Get output item info
    const outputDrop = recipe.drops[0];
    const outputIcon = getItemIcon(outputDrop.itemId);
    const outputName = getItemName(outputDrop.itemId);

    // Get potion effects from item data
    const outputItem = getItem(outputDrop.itemId);
    const potionEffects = outputItem?.effects ? formatPotionEffects(outputItem.effects) : null;

    // Calculate effective time with all bonuses
    const speedMultiplier = alchemySkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.ALCHEMY);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.ALCHEMY);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = recipe.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.ALCHEMY);
    let effectiveXP = recipe.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Ashes bonus info (requires 3 ashes per craft)
    const ashesCount = gameState.getItemCount('ashes');
    const hasAshes = ashesCount >= 3;

    const cardClasses = [
        'potion-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasMaterials && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    // Source breakdown for herbs and vials
    const herbBreakdown = herbCounts.bloomhold > 0
        ? `(${herbCounts.inventory}+${herbCounts.bloomhold}🌱)`
        : '';
    const vialBreakdown = vialCounts.bloomhold > 0
        ? `(${vialCounts.inventory}+${vialCounts.bloomhold}🌱)`
        : '';

    return `
        <div class="${cardClasses}"
             data-potion-id="${recipe.id}"
             style="--potion-color: ${recipe.color}">

            <div class="potion-card-header">
                <span class="potion-card-level">${isUnlocked ? `Lvl ${recipe.level}` : `Req: ${recipe.level}`}</span>
                <span class="potion-card-icon">${outputIcon}</span>
                <span class="potion-card-name">${recipe.name}</span>
            </div>

            <div class="potion-card-info">
                <div class="potion-stat">
                    <span class="stat-icon alchemy-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp">${recipe.xp}</span>
                </div>
                <div class="potion-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="potion-materials">
                <div class="materials-label">Input</div>
                <div class="material-row ${!hasHerbs ? 'missing' : ''}">
                    <span class="material-name">${herbIcon} ${herbName}</span>
                    <span class="material-count">${herbCounts.total}/${recipe.inputQuantity} ${herbBreakdown}</span>
                </div>
                <div class="material-row ${!hasVials ? 'missing' : ''}">
                    <span class="material-name">${vialIcon} ${vialName}</span>
                    <span class="material-count">${vialCounts.total}/${recipe.secondaryQuantity} ${vialBreakdown}</span>
                </div>
            </div>

            <div class="ashes-bonus ${hasAshes ? 'active' : 'inactive'}">
                <div class="ashes-bonus-header">
                    <span class="ashes-label">${getFireSpriteHTML()} Ashes Bonus</span>
                    <span class="ashes-info-icon" title="Auto-consumes 3 ashes per craft when available for 25% chance at double output and XP">ⓘ</span>
                </div>
                <div class="ashes-bonus-content">
                    <span class="ashes-chance">25% double output + XP</span>
                    <span class="ashes-count ${hasAshes ? '' : 'empty'}">${ashesCount}/3 ashes</span>
                </div>
            </div>

            <div class="potion-output">
                <div class="output-label">Output</div>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    <span class="output-qty">x${outputDrop.quantity}</span>
                </div>
            </div>

            ${potionEffects ? `
                <div class="potion-effects">
                    <div class="effects-header">
                        <span class="effects-icon">${potionEffects.icon}</span>
                        <span class="effects-duration">${potionEffects.duration}</span>
                    </div>
                    <div class="effects-list">
                        ${potionEffects.effects.map(e => `<span class="effect-item">${e}</span>`).join('')}
                    </div>
                </div>
            ` : ''}

            <div class="potion-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.alchemyUI.toggleBrewing('${recipe.id}')">
                        ${isActive ? 'Stop' : 'Brew'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="potion-progress">
                <div class="potion-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render tab navigation
 */
function renderTabs() {
    return `
        <div class="skill-tabs">
            <button class="skill-tab ${currentTab === 'cleaning' ? 'active' : ''}"
                    onclick="window.alchemyUI.switchTab('cleaning')">
                <span class="tab-bar-icon silhouette">${getItemIcon('grimy_greenleaf')}</span>Cleaning
            </button>
            <button class="skill-tab ${currentTab === 'potions' ? 'active' : ''}"
                    onclick="window.alchemyUI.switchTab('potions')">
                <span class="tab-bar-icon silhouette">${getItemIcon('feral_draft')}</span>Potions
            </button>
        </div>
    `;
}

/**
 * Render the cleaning tab content
 */
function renderCleaningTab() {
    const playerLevel = alchemySkill.level;
    const currentAction = gameState.getCurrentAction();
    const recipes = Object.values(HERB_CLEANING).sort((a, b) => a.level - b.level);

    const cardsHtml = recipes.map(recipe =>
        renderHerbCard(recipe, playerLevel, currentAction)
    ).join('');

    return `
        <div class="herb-grid">
            ${cardsHtml}
        </div>
    `;
}

/**
 * Render the potions tab content
 */
function renderPotionsTab() {
    const playerLevel = alchemySkill.level;
    const currentAction = gameState.getCurrentAction();
    const recipes = Object.values(POTION_RECIPES).sort((a, b) => a.level - b.level);

    const cardsHtml = recipes.map(recipe =>
        renderPotionCard(recipe, playerLevel, currentAction)
    ).join('');

    return `
        <div class="potion-grid">
            ${cardsHtml}
        </div>
    `;
}

/**
 * Render the alchemy UI
 */
export function renderAlchemyUI(params = {}) {
    if (params.tab) {
        currentTab = params.tab;
    }

    const container = document.getElementById('skill-content');

    let tabContent = '';
    if (currentTab === 'cleaning') {
        tabContent = renderCleaningTab();
    } else if (currentTab === 'potions') {
        tabContent = renderPotionsTab();
    }

    container.innerHTML = `
        <div class="alchemy-interface">
            ${renderCompactHeader()}
            ${renderTabs()}
            <div class="alchemy-content">
                ${tabContent}
            </div>
        </div>
    `;
}

/**
 * Update material counts without full re-render
 */
function updateMaterialCounts() {
    // Update herb cards
    document.querySelectorAll('.alchemy-interface .herb-card').forEach(card => {
        const herbId = card.dataset.herbId;
        if (!herbId) return;

        const recipe = Object.values(HERB_CLEANING).find(h => h.id === herbId);
        if (!recipe) return;

        const counts = getHerbCount(recipe.inputItem);
        const hasMaterials = counts.total >= 1;

        const sourceBreakdown = counts.bloomhold > 0
            ? `(${counts.inventory}+${counts.bloomhold}🌱)`
            : '';

        const countEl = card.querySelector('.material-count');
        if (countEl) countEl.textContent = `${counts.total}/1 ${sourceBreakdown}`;

        const materialRow = card.querySelector('.material-row');
        if (materialRow) materialRow.classList.toggle('missing', !hasMaterials);

        card.classList.toggle('no-materials', !hasMaterials && !card.classList.contains('locked'));
    });

    // Update potion cards
    document.querySelectorAll('.alchemy-interface .potion-card').forEach(card => {
        const potionId = card.dataset.potionId;
        if (!potionId) return;

        const recipe = Object.values(POTION_RECIPES).find(p => p.id === potionId);
        if (!recipe) return;

        const herbCounts = getHerbCount(recipe.inputItem);
        const vialCounts = getItemCountWithBloomhold(recipe.secondaryItem);
        const hasHerbs = herbCounts.total >= recipe.inputQuantity;
        const hasVials = vialCounts.total >= recipe.secondaryQuantity;
        const hasMaterials = hasHerbs && hasVials;

        const rows = card.querySelectorAll('.material-row');
        if (rows[0]) {
            const herbBreakdown = herbCounts.bloomhold > 0
                ? `(${herbCounts.inventory}+${herbCounts.bloomhold}🌱)`
                : '';
            rows[0].querySelector('.material-count').textContent = `${herbCounts.total}/${recipe.inputQuantity} ${herbBreakdown}`;
            rows[0].classList.toggle('missing', !hasHerbs);
        }
        if (rows[1]) {
            const vialBreakdown = vialCounts.bloomhold > 0
                ? `(${vialCounts.inventory}+${vialCounts.bloomhold}🌱)`
                : '';
            rows[1].querySelector('.material-count').textContent = `${vialCounts.total}/${recipe.secondaryQuantity} ${vialBreakdown}`;
            rows[1].classList.toggle('missing', !hasVials);
        }

        card.classList.toggle('no-materials', !hasMaterials && !card.classList.contains('locked'));
    });
}

/**
 * Update the alchemy UI (called each tick)
 */
export function updateAlchemyUI() {
    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.ALCHEMY);
    const xpPerHour = getXPPerHour(SKILLS.ALCHEMY);
    const { levelDisplay } = getSkillProgressInfo(SKILLS.ALCHEMY);

    const xpTotalEl = document.querySelector('.alchemy-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.alchemy-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    const levelEl = document.querySelector('.alchemy-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    updateCompactHeaderProgress('.alchemy-interface', SKILLS.ALCHEMY);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.alchemy-interface');

    // Update material counts
    updateMaterialCounts();

    // Check for stale progress bars
    const action = gameState.getCurrentAction();
    const isAlchemyActive = action && action.type === SKILLS.ALCHEMY;

    const existingActiveCards = document.querySelectorAll('.alchemy-interface .herb-card.active, .alchemy-interface .potion-card.active');
    if (existingActiveCards.length > 0 && !isAlchemyActive) {
        renderAlchemyUI();
        return;
    }

    // Update action progress
    if (!isAlchemyActive) return;

    if (action.subType === 'cleaning') {
        const activeCard = document.querySelector(`.herb-card[data-herb-id="${action.recipe.id}"]`);
        if (activeCard) {
            const progressFill = activeCard.querySelector('.herb-progress-fill');
            if (progressFill) {
                progressFill.style.width = `${action.progress * 100}%`;
            }
        }
    } else if (action.subType === 'potion') {
        const activeCard = document.querySelector(`.potion-card[data-potion-id="${action.recipe.id}"]`);
        if (activeCard) {
            const progressFill = activeCard.querySelector('.potion-progress-fill');
            if (progressFill) {
                progressFill.style.width = `${action.progress * 100}%`;
            }
        }
    }
}

/**
 * Initialize the alchemy UI
 */
export function initAlchemyUI() {
    alchemySkill.initializeStartingTool();

    alchemySkill.onCleaningComplete = (recipe, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Alchemy level ${xpResult.newLevel}!`);
        }

        if (getCurrentSkillId() === SKILLS.ALCHEMY && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderAlchemyUI();
            }
        }
    };

    alchemySkill.onBrewingComplete = (recipe, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Alchemy level ${xpResult.newLevel}!`);
        }

        if (getCurrentSkillId() === SKILLS.ALCHEMY && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderAlchemyUI();
            }
        }
    };

    window.alchemyUI = {
        toggleCleaning: (herbId) => {
            const result = alchemySkill.toggleCleaning(herbId);
            if (!result) {
                const { reason } = alchemySkill.canCleanHerb(herbId);
                if (reason) toastWarning(reason);
            }
            renderAlchemyUI();
        },
        toggleBrewing: (potionId) => {
            const result = alchemySkill.toggleBrewing(potionId);
            if (!result) {
                const { reason } = alchemySkill.canBrewPotion(potionId);
                if (reason) toastWarning(reason);
            }
            renderAlchemyUI();
        },
        upgradeMortar: (mortarId) => {
            const result = alchemySkill.upgradeMortar(mortarId);
            if (result.success) {
                renderAlchemyUI();
            } else {
                toastWarning(result.reason);
            }
        },
        switchTab: (tabId) => {
            currentTab = tabId;
            localStorage.setItem(ALCHEMY_TAB_KEY, tabId);
            renderAlchemyUI();
        }
    };

    // Listen for talent tree close to re-render alchemy UI
    window.addEventListener('talentTreeClosed', (e) => {
        if (e.detail?.skillId === SKILLS.ALCHEMY && getCurrentSkillId() === SKILLS.ALCHEMY) {
            renderAlchemyUI();
        }
    });
}

/**
 * Cleanup the alchemy UI
 */
export function cleanupAlchemyUI() {
    // UI-only cleanup — do NOT stop the alchemy action here.
    // Actions should continue running when navigating away from the skill.
}

export default {
    renderAlchemyUI,
    updateAlchemyUI,
    initAlchemyUI,
    cleanupAlchemyUI
};
